import * as React from "react";
import {useEffect, useState} from "react";
import { cn } from "@/lib/utils";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Trash2, Pencil, RotateCcw, KeyRound } from "lucide-react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import { toast } from "sonner";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import UsersRepository from "../../repositories/UsersRepository";
import {User, UserRole, UserRoleColors, UserRoleIcons, UserRoleLabels} from "../../models/User";
import EditUserModal from "./EditUserModal";
import {FilterCombobox} from "../common/FilterCombobox";
import TablePagination from "../common/Table/TablePagination";
import TableEmptyState from "../common/TableEmptyState";
import { SearchInput } from "../common/Table/SearchInput";
import {useTableHandlers} from "../common/Table/Hooks/UseTableHandlers";
import {
  fileFiltersBoxClassName,
  filterComboboxClassName,
  tableWrapperClassName,
} from "../common/Table/TableStyles";
import {QUERY_CONFIG} from "../../constants/QueryConfig";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import { useAuth } from "../../contexts/AuthContext";

interface UsersTableProps {
  onDeleteUser: (userId: string, username: string) => void;
  onReactivateUser: (userId: string, username: string) => void;
  onResetPassword: (userId: string, username: string, email: string | null) => void;
}

export const UsersTable: React.FC<UsersTableProps> = ({ onDeleteUser, onReactivateUser, onResetPassword }) => {
  const usersRepository = new UsersRepository();
  const queryClient = useQueryClient();
  const currentUserId = useAuth().getUserId();
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = React.useState<string | null>(null);
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const {
    order,
    page,
    rowsPerPage,
    rows,
    sort,
    setPage,
    setRows,
    setSort,
    changeOrder,
    handleRowsPerPageChange
  } = useTableHandlers({
    order: "asc",
    sort: "username",
    page: 0,
    rowsPerPage: 50
  });

  const usersQuery = useQuery({ queryKey: [
      QUERY_KEYS.USERS,
      roleFilter,
      searchQuery,
      page,
      rowsPerPage,
      sort,
      order,
      showDeactivated,
    ], queryFn:
    async () => await usersRepository.getUsers({
      roles: roleFilter
        ? [roleFilter as UserRole]
        : [UserRole.Admin, UserRole.Editor, UserRole.Viewer],
      searchPhrase: searchQuery,
      descending: order === "desc",
      orderBy: sort,
      pageNumber: page + 1,
      pageSize: rowsPerPage,
      showDeactivated,
    }), staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
      refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (usersQuery.data) {
      setUsers(usersQuery.data.data);
      setRows(usersQuery.data.paging?.totalCount ?? 0);
    }
  }, [usersQuery.data]);

  const handleSearch = (organizationName: string) => {
    setSearchQuery(organizationName ?? null);
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setSelectedUser(null);
    setEditModalOpen(false);
  };

  const handleEditUser = async (updatedUser: User) => {
    try {
      await usersRepository.updateUser(updatedUser.id, updatedUser);
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to update user");
      throw error;
    }
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_NAVIGATION_INFO] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ASSIGNABLE_USERS] }),
    ]);
    toast.success(`User "${updatedUser.username}" updated`);
    closeEditModal();
  };

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <EditUserModal
        open={editModalOpen}
        handleClose={closeEditModal}
        user={selectedUser}
        onEditUser={handleEditUser}
      />
      <div className={fileFiltersBoxClassName}>
        <SearchInput
          label="Search users"
          placeholder="Username or email"
          onSearch={(value) => handleSearch(value)}
        />
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Role</Label>
          <FilterCombobox
            value={roleFilter}
            onChange={(newValue) => {
              setPage(0);
              setRoleFilter(newValue);
            }}
            placeholder="Role"
            options={Object.values(UserRole).map((status) => {
              const userRole = status as UserRole;
              return {
                value: userRole,
                label: UserRoleLabels[userRole],
                color: UserRoleColors[userRole],
                icon: UserRoleIcons[userRole],
              };
            })}
          />
        </div>
        <div className={filterComboboxClassName}>
          <Label className="text-xs font-semibold text-muted-foreground">Status</Label>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={showDeactivated ? "deactivated" : "active"}
            onValueChange={(val) => {
              if (val) {
                setPage(0);
                setShowDeactivated(val === "deactivated");
              }
            }}
          >
            <ToggleGroupItem value="active">Active</ToggleGroupItem>
            <ToggleGroupItem value="deactivated">Deactivated</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
      <div className={cn(tableWrapperClassName, (usersQuery.isFetching || usersQuery.isError) && "opacity-50 pointer-events-none")}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[28%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Username"}
                  sortProperty={"username"}
                  getSort={() => sort}
                  setSort={(newSort) => setSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[28%] px-1.5 py-3">
                <SortableTableColumnHeader
                  label={"Email Address"}
                  sortProperty={"email"}
                  getSort={() => sort}
                  setSort={(newSort) => setSort(newSort)}
                  getOrder={() => order}
                  setOrder={(newOrder) => changeOrder(newOrder)}
                />
              </TableHead>
              <TableHead className="w-[20%] px-1.5 py-3">Role</TableHead>
              <TableHead className="w-[12%] px-1.5 py-3">Status</TableHead>
              <TableHead className="w-[50px]" />
              <TableHead className="w-[50px]" />
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => {
              const isDeactivated = user.isDeactivated;
              return (
                <TableRow key={user.id}>
                  <TableCell>
                    <span className="text-sm break-words">
                      {user.username}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm break-words">
                      {user.email}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={UserRoleColors[user.role]}>
                      {(() => { const Icon = UserRoleIcons[user.role]; return Icon ? <Icon className="size-3" /> : null; })()}
                      {UserRoleLabels[user.role]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={isDeactivated ? "danger" : "success"}>
                      {isDeactivated ? "Deactivated" : "Active"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Edit"
                            onClick={() => openEditModal(user)}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit user</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    {isDeactivated ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="text-emerald-600 dark:text-emerald-400"
                              aria-label="Reactivate"
                              onClick={() => onReactivateUser(user.id ?? "", user.username ?? "")}
                            >
                              <RotateCcw className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reactivate user</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Reset password"
                              onClick={() => onResetPassword(user.id ?? "", user.username ?? "", user.email)}
                            >
                              <KeyRound className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Reset password</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </TableCell>
                  <TableCell>
                    {/* The server refuses to deactivate the signed-in account, so its row offers no Delete. */}
                    {!isDeactivated && user.id !== currentUserId && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="text-destructive"
                              aria-label="Delete"
                              onClick={() => onDeleteUser(user.id ?? "", user.username ?? "")}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete user</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
              {users.length === 0 && !usersQuery.isFetching && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={99}>
                    <TableEmptyState title="No users found" />
                  </TableCell>
                </TableRow>
              )}
          </TableBody>
        </Table>
        <TablePagination
          page={page}
          rowsPerPage={rowsPerPage}
          rows={rows}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
    </div>
  );
};
